import React from 'react';

// Utils
import { FormattedMessage } from '../../util/reactIntl.js';
import { types as sdkTypes } from '../../util/sdkLoader.js';
import { formatMoney } from '../../util/currency.js';
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';
import { getListingValuation, maskVin } from '../../util/emotoValuation.js';
import { formatLocationDisplay } from '../../components/ListingCard/ListingCard.helpers.js';

import { Heading } from '../../components';
import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';

import css from './ListingPage.module.css';

const { Money } = sdkTypes;

/**
 * Scannable two-column spec table for the "detail" style listing fields
 * (schemaType enum / long / boolean with showConfig.isDetail).
 *
 * This is intentionally generic: every row comes from the listingFields
 * configuration, which in production is authored in Sharetribe Console. No
 * field key is hardcoded here.
 *
 * @param {Object} props
 * @param {Array<{key: string, label: React.ReactNode, value: React.ReactNode}>} props.details
 * @returns {JSX.Element|null}
 */
const SpecTable = props => {
  const { details } = props;

  if (!details?.length) {
    return null;
  }

  return (
    <section className={css.specSection}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.detailsTitle" />
      </Heading>
      <ul className={css.specTable}>
        {details.map(detail => (
          <li key={detail.key} className={css.specRow}>
            <span className={css.specLabel}>{detail.label}</span>
            <span className={css.specValue}>{detail.value}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

/**
 * Resolves Brand, Model, or Part Category from the category hierarchy so they can
 * be displayed prominently in the SpecTable on the listing detail page.
 */
const getCategoryDetails = (categoriesObj, categoryConfig = [], intl, listingType) => {
  const details = [];
  if (!Array.isArray(categoryConfig) || !categoryConfig.length || !categoriesObj) {
    return details;
  }

  const cat1Id = categoriesObj.categoryLevel1;
  const cat2Id = categoriesObj.categoryLevel2;
  const cat3Id = categoriesObj.categoryLevel3;

  const cat1 = categoryConfig.find(c => c.id === cat1Id);
  const cat2 = cat1?.subcategories?.find(c => c.id === cat2Id);
  const cat3 = cat2?.subcategories?.find(c => c.id === cat3Id);

  const isEmoto = listingType === 'emoto-sale' || cat1Id === 'emoto' || cat1Id === 'emotos';
  const isParts = listingType === 'parts-sale' || cat1Id === 'parts';

  if (isEmoto) {
    if (cat2?.name) {
      details.push({
        key: 'brand',
        label: intl.formatMessage({ id: 'ListingPage.brandLabel', defaultMessage: 'Brand' }),
        value: cat2.name,
      });
    }
    if (cat3?.name) {
      details.push({
        key: 'model',
        label: intl.formatMessage({ id: 'ListingPage.modelLabel', defaultMessage: 'Model' }),
        value: cat3.name,
      });
    }
  } else if (isParts) {
    if (cat2?.name) {
      details.push({
        key: 'partCategory',
        label: intl.formatMessage({
          id: 'ListingPage.partCategoryLabel',
          defaultMessage: 'Part category',
        }),
        value: cat2.name,
      });
    }
  } else {
    if (cat2?.name) {
      details.push({
        key: 'category',
        label: intl.formatMessage({ id: 'ListingPage.categoryLabel', defaultMessage: 'Category' }),
        value: cat2.name,
      });
    }
    if (cat3?.name) {
      details.push({
        key: 'subcategory',
        label: intl.formatMessage({
          id: 'ListingPage.subcategoryLabel',
          defaultMessage: 'Subcategory',
        }),
        value: cat3.name,
      });
    }
  }

  return details;
};

/**
 * Resolves Delivery options and shipping fee from publicData for the SpecTable.
 */
const getDeliveryDetails = (publicData, currency = 'USD', intl) => {
  const details = [];
  if (!publicData) {
    return details;
  }
  const { pickupEnabled, shippingEnabled, shippingPriceInSubunitsOneItem } = publicData;
  if (!pickupEnabled && !shippingEnabled) {
    return details;
  }

  let deliveryValue = null;
  if (pickupEnabled && shippingEnabled) {
    deliveryValue = intl.formatMessage({
      id: 'ListingPage.deliveryPickupAndShipping',
      defaultMessage: 'Local pickup & Shipping',
    });
  } else if (pickupEnabled) {
    deliveryValue = intl.formatMessage({
      id: 'ListingPage.deliveryPickupOnly',
      defaultMessage: 'Local pickup only',
    });
  } else if (shippingEnabled) {
    deliveryValue = intl.formatMessage({
      id: 'ListingPage.deliveryShippingOnly',
      defaultMessage: 'Nationwide shipping',
    });
  }

  if (deliveryValue) {
    details.push({
      key: 'delivery',
      label: intl.formatMessage({
        id: 'ListingPage.deliveryLabel',
        defaultMessage: 'Delivery',
      }),
      value: deliveryValue,
    });
  }

  if (shippingEnabled && shippingPriceInSubunitsOneItem != null) {
    try {
      const formattedShipping = formatMoney(
        intl,
        new Money(shippingPriceInSubunitsOneItem, currency)
      );
      details.push({
        key: 'shippingFee',
        label: intl.formatMessage({
          id: 'ListingPage.shippingFeeLabel',
          defaultMessage: 'Shipping fee',
        }),
        value: formattedShipping,
      });
    } catch (e) {
      // ignore formatting errors
    }
  }

  return details;
};

/**
 * Renders custom listing fields.
 * - The spec table (above) is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, currency, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration || {};
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData?.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    // isFieldForCategory's signature is (categories, fieldConfig). Passing the field config
    // as the FIRST argument made every category-limited field fall back to `!isLimited`,
    // i.e. the spec table showed emoto fields on parts listings and vice versa. It also
    // contradicted the card specs, which filter by the real category path.
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData?.[key];
    const metadataValue = metadata?.[key];
    const value = typeof publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const isVin = key === 'vin' || key === 'serialNumber' || key.toLowerCase().includes('vin');
      const safeValue = isVin && typeof value === 'string' ? maskVin(value) : value;

      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        safeValue,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  const categoryDetails = getCategoryDetails(
    categoriesObj,
    listingCategoriesConfig,
    intl,
    publicData?.listingType
  );

  const valuation = getListingValuation({ attributes: { publicData } });
  const valuationDetails = valuation
    ? [
        {
          key: 'valuationEstimate',
          label: intl.formatMessage({
            id: 'ListingPage.valuationEstimateLabel',
            defaultMessage: 'Est. Market Value',
          }),
          value: `${valuation.formattedRange} (Private Party)`,
        },
      ]
    : [];

  // Detail-style fields are rendered by the local SpecTable so that they can use
  // the scannable two-column grid. Everything else keeps the shared rendering.
  const extendedDetails = listingFieldConfigs?.reduce(pickExtendedDataFields, []) || [];
  const rawLocation = publicData?.location?.address || publicData?.location?.search;
  const formattedLoc = formatLocationDisplay(rawLocation);
  const locationDetails = formattedLoc
    ? [
        {
          key: 'location',
          label: intl.formatMessage({
            id: 'ListingPage.locationLabel',
            defaultMessage: 'Location',
          }),
          value: formattedLoc,
        },
      ]
    : [];
  const deliveryDetails = getDeliveryDetails(publicData, currency, intl);
  const details = [
    ...categoryDetails,
    ...valuationDetails,
    ...extendedDetails,
    ...locationDetails,
    ...deliveryDetails,
  ];

  return (
    <>
      <SpecTable details={details} />
      <CustomExtendedDataSection
        // fieldConfigs is intentionally omitted: the shared SectionDetails
        // renders nothing without it, and SpecTable takes over that job.
        sectionDetailsProps={{}}
        propsForCustomFields={propsForCustomFields}
        idPrefix="listingPage"
        pickExtendedDataFields={pickExtendedDataFields}
        rootClassName={css.specSection}
      />
    </>
  );
};

export default CustomListingFields;
